<template>
  <v-card v-bind="computedCardProps">
    <v-toolbar flat color="white" :dense="toolbarDense">
      <v-toolbar-title>{{ title }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-col cols="12" sm="4">
        <v-text-field
          v-if="searchBar"
          v-model="computedSearchText"
          filled
          rounded
          dense
          prepend-inner-icon="mdi-magnify"
          clearable
          class="mt-6"
        ></v-text-field>
      </v-col>
      <slot
        name="add"
        :addItem="addItem"
        :addItemMultiple="addItemMultiple"
        :replaceItemMultiple="replaceItemMultiple"
      >
        <v-btn icon v-if="allowEdit" @click="addItem" :disabled="disabled">
          <v-icon>add</v-icon>
        </v-btn>
      </slot>
      <slot name="append-top"></slot>
    </v-toolbar>
    <v-card-text>
      <v-data-table
        :headers="modifiedHeaders"
        :items="items"
        item-key="id"
        :show-expand="!noExpand"
        :expanded.sync="expandedItems"
        :search="computedSearchText"
        :dense="dense"
        @change="saveItems"
        @current-items="setFilteredItems"
      >
        <template #item.name="props">
          <v-text-field
            v-model="props.item.name"
            label="Name"
            single-line
            :dense="dense"
            :error-messages="$utils.getIfExists(errorMessages, ['name', 'messages'])"
            @change="$emit('change')"
          ></v-text-field>
        </template>

        <template v-for="(_, name) in $slots" #[name]>
          <slot :name="name" />
        </template>

        <!-- pass through scoped slots -->
        <template v-for="(_, scopedName) in $scopedSlots" #[scopedName]="props">
          <slot
            :name="scopedName"
            v-bind="props"
            :allowEdit="allowEdit"
            :errors="$utils.getIfExists(errorMessages, [items.indexOf(props.item)])"
          />
        </template>

        <template #item.actions="props">
          <v-tooltip v-if="copyBtn" top>
            <template #activator="{ on }">
              <v-icon icon v-on="on" @click="copyItem(items.indexOf(props.item))">
                mdi-content-copy
              </v-icon>
            </template>
            <span>Copy</span>
          </v-tooltip>
          <v-icon
            v-if="allowEdit"
            icon
            :disabled="!allowEdit || props.item.noDelete"
            @click="deleteItem(items.indexOf(props.item))"
            >delete
          </v-icon>
        </template>

        <template v-if="!noExpand" #expanded-item="{ item, headers }">
          <td :colspan="headers.length" class="pa-1">
            <slot
              name="default"
              :item="item"
              :itemIndex="item.id"
              :errors="$utils.getIfExists(errorMessages, [items.indexOf(item)])"
            ></slot>
          </td>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "Aggregate",
  model: {
    prop: "items",
  },
  components: {},
  props: {
    title: {
      type: String,
      required: false,
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    ItemConstructor: {
      type: Function,
      required: true,
    },
    itemDefaults: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    allowEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    copyBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: false,
    },
    headers: {
      type: Array,
      required: false,
      default() {
        return [{ text: "Name", value: "name" }];
      },
    },
    noExpand: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorMessages: {
      type: Object,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchBar: { type: Boolean, required: false, default: false },
    searchText: { type: String, required: false, default: "" },
    autoExpandSearchResult: { type: Boolean, required: false, default: false },
    cardProps: { type: Object, required: false, default: () => ({}) },
    toolbarDense: { type: Boolean, required: false, default: false },
  },
  computed: {
    hasExpand() {
      return this.$slots.default;
    },
    lastObjectIndex() {
      return this.items.length - 1;
    },
    modifiedHeaders() {
      const headers = [{ text: "ID", value: "id", sortable: false, width: 20 }, ...this.headers];
      if (!this.noExpand) {
        headers.unshift({
          value: "data-table-expand",
          sortable: false,
          width: 20,
        });
      }
      if (this.allowEdit) {
        headers.push({ text: "Actions", value: "actions", sortable: false });
      }
      return headers.map((e) => {
        if (e.width == null) return { ...e, width: 120 };
        return e;
      });
    },
    computedSearchText: {
      get() {
        return this.searchText;
      },
      set(v) {
        this.$emit("update:searchText", v);
      },
    },
    searchResultExact() {
      if (this.autoExpandSearchResult && this.searchBar) {
        return this.filteredItems.length === 1;
      }
      return false;
    },
    computedCardProps() {
      return { class: ["ma-3"], ...this.cardProps };
    },
  },
  methods: {
    addItem() {
      const obj = new this.ItemConstructor(this.itemDefaults);
      obj.id = this.getNextId();
      obj.name = `${obj.name} ${obj.id}`;

      const itemsCopy = [...this.items, obj];
      this.saveItems(itemsCopy);
    },
    deleteItem(idx) {
      const itemsCopy = this.items;
      itemsCopy.splice(idx, 1);
      this.saveItems(itemsCopy);
    },
    copyItem(idx) {
      const itemsCopy = this.$utils.deepClone(this.items);
      const newItem = this.$utils.deepClone(itemsCopy[idx]);
      newItem.id = this.getNextId();
      newItem.name = this.getCopyName(newItem.name);
      itemsCopy.push(newItem);
      this.saveItems(itemsCopy);
    },
    addItemMultiple(items) {
      const newItems = items.map(
        (item, i) => new this.ItemConstructor({ ...item, id: this.getNextId(i) })
      );
      const itemsCopy = [...this.items, ...newItems];
      this.saveItems(itemsCopy);
    },
    replaceItemMultiple(items) {
      const newItems = items.map(
        (item, i) => new this.ItemConstructor({ ...item, id: this.getNextId(i) })
      );
      const itemsCopy = [...this.items.slice(0, 4), ...newItems];
      this.saveItems(itemsCopy);
    },
    saveItems(newItems = null) {
      this.$emit("input", newItems || this.items);
      this.$emit("change");
    },
    getNextId(offset = 0) {
      if (!this.items.length) return 1;
      return Math.max(...this.items.map((e) => e.id)) + 1 + offset;
    },
    getCopyName(itemName) {
      let name = itemName;
      const itemsNames = this.items.map((i) => i.name);
      while (itemsNames.includes(name)) {
        const suffix = this.getCopySuffix(name);
        name =
          suffix === "(1)"
            ? `${name} ${suffix}`
            : name.replace(name.slice(name.lastIndexOf("(")), suffix);
      }
      return name;
    },
    getCopySuffix(name) {
      let suffix = "(1)";
      // Try to find (number)
      if (name.includes("(")) {
        const nameEnd = name.slice(name.lastIndexOf("("));
        // if nameEnd ends with ')'
        if (nameEnd.endsWith(")")) {
          // If text between '(' ')' is a number
          const index = Number(nameEnd.slice(1, -1));
          if (index) {
            suffix = `(${index + 1})`;
          }
        }
      }
      return suffix;
    },
    setFilteredItems(e) {
      this.filteredItems = e;
    },
  },
  data() {
    return {
      filteredItems: [],
      expandedItems: [],
    };
  },
  watch: {
    searchResultExact(v) {
      if (v) {
        this.expandedItems = this.filteredItems;
      }
    },
  },
};
</script>

<style></style>
